/* API utility — centralized fetch wrapper with auth token handling. */
const API_URL = process.env.REACT_APP_API_URL || "";

// Auth-related localStorage keys. Kept in one place so logout / expired-token
// paths only wipe the session, not every other pref (theme, page-size, etc.).
export const AUTH_STORAGE_KEYS = [
  "access_token", "refresh_token", "impersonating",
  "original_token", "original_refresh_token",
];
export function clearAuthStorage() {
  for (const k of AUTH_STORAGE_KEYS) localStorage.removeItem(k);
}

// FastAPI returns 422 validation errors as { detail: [{loc, msg, type, ...}] }.
// Without flattening, `new Error(arr)` stringifies to "[object Object]" in the UI.
// Exported for the public pages (CI survey, sign, survey, reset password),
// which call `fetch` directly because they have no session; each passes
// its own `fallback` wording. (api-contract audit 2026-09-24 F12)
export function formatApiError(err, status, fallback = `HTTP ${status}`) {
  if (!err) return fallback;
  if (typeof err.detail === "string") return err.detail;
  if (Array.isArray(err.detail)) {
    return err.detail
      .map((e) => {
        const path = Array.isArray(e.loc)
          ? e.loc.filter((p) => p !== "body").join(".")
          : "";
        return path ? `${path}: ${e.msg}` : e.msg;
      })
      .join("; ");
  }
  return fallback;
}

export async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("access_token");
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Try refresh
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${localStorage.getItem("access_token")}`;
      const retry = await fetch(`${API_URL}${path}`, { ...options, headers });
      return handleResponse(retry);
    }
    clearAuthStorage();
    window.location.href = "/login";
    throw new Error("Session expired");
  }

  return handleResponse(res);
}

// One tail for the first attempt and the post-refresh retry. The retry
// used to have its own copy without the 204 line, so a DELETE that
// succeeded right after the hourly token refresh failed on
// `retry.json()` and showed an error; it also threw a bare `HTTP nnn`
// in place of the server's detail. (api-contract audit 2026-09-24 F6)
async function handleResponse(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(formatApiError(err, res.status));
  }

  if (res.status === 204) return null;
  return res.json();
}

async function tryRefresh() {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_URL}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

export async function apiFetchRaw(path, options = {}) {
  const token = localStorage.getItem("access_token");
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${localStorage.getItem("access_token")}`;
      const retry = await fetch(`${API_URL}${path}`, { ...options, headers });
      // Match apiFetch semantics — if the retry still fails, surface a
      // real error instead of returning a broken Response the caller
      // will silently treat as OK. (audit finding A11)
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ detail: `HTTP ${retry.status}` }));
        throw new Error(formatApiError(err, retry.status));
      }
      return retry;
    }
    clearAuthStorage();
    window.location.href = "/login";
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(formatApiError(err, res.status));
  }
  return res;
}

export const api = {
  get: (path) => apiFetch(path),
  getRaw: (path) => apiFetchRaw(path),
  post: (path, body) => apiFetch(path, { method: "POST", body: JSON.stringify(body) }),
  put: (path, body) => apiFetch(path, { method: "PUT", body: JSON.stringify(body) }),
  delete: (path) => apiFetch(path, { method: "DELETE" }),
  // Multipart upload — has its own path because FormData needs the
  // browser to set Content-Type (with boundary) instead of the JSON
  // default. Shares the same 401 → refresh → retry flow as apiFetch
  // so a resume upload that hits an expired-token boundary refreshes
  // and completes instead of failing "Upload failed: 401" without
  // telling the user their session recovered. (audit finding CS5)
  upload: async (path, file) => {
    const form = new FormData();
    form.append("file", file);
    const doUpload = () => {
      const token = localStorage.getItem("access_token");
      return fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
    };
    let res = await doUpload();
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        res = await doUpload();
      } else {
        clearAuthStorage();
        window.location.href = "/login";
        throw new Error("Session expired");
      }
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
      throw new Error(formatApiError(err, res.status));
    }
    return res.json();
  },
};
